import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import functions from '../firebase/functions';
import { useAuth } from '../auth/useAuth';
import { usagePageKey } from '../utils/usageKeys';
import { isTestAccount } from '../utils/testData';

/**
 * Zaehlt einen Seitenaufruf.
 *
 * Haengt EINMAL im `Layout` und feuert bei jedem Routenwechsel - nicht in
 * den einzelnen Seiten: Ein vergessener Aufruf in einer neuen Komponente
 * waere eine Luecke, die niemandem auffiele, bis die Zahl unerklaerlich
 * niedrig ist.
 *
 * ── Was hier bewusst NICHT passiert ─────────────────────────────────────
 * Es wird kein Pfad verschickt, sondern ein Schluessel aus einer festen
 * Liste (`usagePageKey`). `/intern/crew/0vXjgExn…` wird zu `crew-profil` -
 * damit verlaesst die uid des Angesehenen den Browser gar nicht erst.
 *
 * Nichts wird gespeichert, kein Cookie, keine Kennung. Der Server legt nur
 * Tageszaehler an (siehe functions/handlers/usageHandlers.js).
 */
export function usePageView() {
  const { pathname } = useLocation();
  const { user, profile, loading } = useAuth();
  const zuletzt = useRef(null);

  useEffect(() => {
    if (!functions) return;
    // Erst zaehlen, wenn feststeht, ob jemand angemeldet ist - sonst landet
    // jeder erste Aufruf eines Mitglieds in der Gaeste-Spalte.
    if (loading) return;
    // Und erst, wenn bei einem Angemeldeten auch das PROFIL da ist. Es kommt
    // ueber einen eigenen onSnapshot und trifft nach dem Auth-Zustand ein -
    // in dem Fenster dazwischen ist `profile` noch null, und die
    // Testkonto-Sperre eine Zeile tiefer liefe ins Leere.
    if (user && !profile) return;

    const key = usagePageKey(pathname);

    // Der Admin-Bereich zaehlt nicht mit. Die Frage ist, wie die Seite
    // GENUTZT wird; Christians eigene Verwaltungsklicks wuerden die Zahlen
    // eines 69-Mitglieder-Clubs sichtbar verzerren.
    if (key === 'admin') return;
    // Das Testkonto ebenso wenig - es prueft die Seite, es benutzt sie nicht.
    if (isTestAccount(profile)) return;

    // React montiert im Entwicklungsmodus jeden Effekt doppelt. Ohne diese
    // Sperre zaehlte jeder Aufruf lokal zweimal.
    if (zuletzt.current === pathname) return;
    zuletzt.current = pathname;

    // Fire-and-forget: Das Ergebnis interessiert niemanden, und ein Fehler
    // darf die Seite nicht stoeren. Der Server wirft ohnehin nie.
    httpsCallable(functions, 'trackUsage')({ seite: key }).catch(() => {});
  }, [pathname, loading, user, profile]);
}

/**
 * Eine Anmeldung zaehlen.
 *
 * Getrennt vom Seitenaufruf, weil es keine Route dafuer gibt: Nach dem
 * Anmelden landet man auf `/intern`, und das ist von einem gewoehnlichen
 * Besuch der Uebersicht nicht zu unterscheiden.
 *
 * Firebase Auth fuehrt zwar `lastSignInTime`, aber nur den LETZTEN - eine
 * Historie gibt es dort nicht. Deshalb meldet es der Client.
 */
export function trackLogin() {
  if (!functions) return;
  httpsCallable(functions, 'trackUsage')({ seite: 'anmelden', ereignis: 'login' }).catch(() => {});
}
